// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// COB-272 (FIND-009): the login-loss acknowledgement for a new Web3Auth
// (delegated custody) account. A Web3Auth key is reconstructed through the
// user's social login — there is no seed phrase — so permanently losing that
// login permanently loses the signing key, and nobody (Cobault included) can
// rebuild it. Registration is therefore gated on an explicit acknowledgement:
// `loginWithWeb3Auth` stashes the live agent + register parameters and throws
// `Web3AuthLossAckRequiredError`; the chooser routes here, and Continue fires
// the deferred register via `completeWeb3AuthRegistration`.
//
// The provider-less path (email capture, COB-345) registers from its own
// panel instead, so that panel embeds the same `Web3AuthLossWarning` +
// checkbox rather than stacking a second interstitial after the email field.

import { useState } from 'preact/hooks';
import { useSession, EmailHasLocalAccountError } from '../session-context.js';
import '../styles/home.css';
import '../styles/accept.css';

/** The acknowledgement checkbox label — shared verbatim by this panel and
 *  the email-capture panel so the two new-account paths attest to the same
 *  sentence. Exported for the panels and their tests. */
export const WEB3AUTH_LOSS_ACK_LABEL =
  'I understand that losing access to my login means losing this signing ' +
  'key, and that no one — including Cobault — can recover it for me.';

/** The login-loss warning shown before EITHER new-account Web3Auth register
 *  (provider-email panel below, typed-email capture panel). Hook-free so it
 *  render-tests without a session provider. */
export function Web3AuthLossWarning() {
  return (
    <div class="seed-warning">
      <p>
        <strong>Your login is your key.</strong> Your Bitcoin signing key is
        reconstructed through this login — there is no seed phrase to write
        down, and Cobault never holds the key.
      </p>
      <p>
        If you permanently lose access to the login itself, you permanently
        lose this signing key. No one — not Cobault, not MetaMask/Web3Auth —
        can rebuild it for you.
      </p>
      <p>
        Before depositing, make sure the login is recoverable on its own:
        set up its backup email, phone number, or two-factor recovery with
        the provider.
      </p>
    </div>
  );
}

/** Map a `completeWeb3AuthRegistration` failure to user-facing copy. The
 *  one-account-per-email collision never reaches this — the panel bounces
 *  it to Self Custody via `onCollision` — so this only dresses the generic
 *  path (mirrors `web3authEmailCaptureErrorMessage`). Exported for unit
 *  tests. */
export function web3authLossAckErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message || 'Could not finish creating your account. Please try again.';
  }
  return 'Could not finish creating your account. Please try again.';
}

export function Web3AuthLossAck({
  onBack,
  onCollision,
}: {
  onBack: () => void;
  /** One-account-per-email 409: route to Self Custody with the email
   *  prefilled (the same bounce the chooser applies — COB-345). */
  onCollision: (email: string) => void;
}) {
  const session = useSession();
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleContinue() {
    setError(null);
    setBusy(true);
    try {
      await session.completeWeb3AuthRegistration();
      // Success = authenticated. The parent page's post-auth gate unmounts
      // this panel; nothing more to do here.
    } catch (err) {
      if (err instanceof EmailHasLocalAccountError) {
        onCollision(err.email);
        return;
      }
      setError(web3authLossAckErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function handleBack() {
    // Tear down the stashed agent + half-committed session state before
    // returning to the chooser, so a fresh attempt starts clean.
    session.cancelWeb3AuthPending();
    onBack();
  }

  return (
    <div class="login-chooser login-chooser--minimal">
      <h2 class="login-chooser-title">Before you create your account</h2>
      <p class="accept-subtitle">
        {session.userEmail ? (
          <>
            You’re creating a Cobault account for{' '}
            <strong>{session.userEmail}</strong>, secured by your login
            provider.
          </>
        ) : (
          'You’re creating a Cobault account secured by your login provider.'
        )}
      </p>

      <Web3AuthLossWarning />

      <label class="loss-ack-row">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) =>
            setAcknowledged((e.target as HTMLInputElement).checked)
          }
          disabled={busy}
        />
        <span>{WEB3AUTH_LOSS_ACK_LABEL}</span>
      </label>

      {error && <p class="offer-error">{error}</p>}

      <div class="accept-actions">
        <button
          type="button"
          class="btn btn-secondary"
          onClick={handleBack}
          disabled={busy}
        >
          Back
        </button>
        <button
          type="button"
          class="btn btn-primary"
          onClick={handleContinue}
          disabled={busy || !acknowledged}
          aria-busy={busy}
        >
          {busy ? 'Creating…' : 'Create account'}
        </button>
      </div>
    </div>
  );
}
